import { useEffect, useMemo, useState } from 'react';
import { AritoDialog, AritoForm, AritoIcon, BottomActionBarV2, ErpDataTableV2 } from '@/components/custom/erp';
import { buildBulkDeleteTables, ErpDataTableBulkDeleteActionBar, notifyBulkDeleteError } from './shared';
import { useAuth } from '@/contexts/auth-context';
import { cn } from '@/lib/utils';
import api from '@/lib/api';

interface BulkDeleteDialogProps {
  title: string;
  onClose: () => void;
  columns?: any[];
  dataRows?: any[];
  bulkDeleteEndpoint?: string;
  handleDelete?: (uuid: string) => void | Promise<void>;
  handleDeleteMany?: (uuids: string[]) => void | Promise<void>;
  onComplete?: () => void | Promise<void>;
}

export const BulkDeleteDialogV4 = ({
  title,
  onClose,
  columns,
  dataRows,
  bulkDeleteEndpoint,
  handleDelete,
  handleDeleteMany,
  onComplete
}: BulkDeleteDialogProps) => {
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [rows, setRows] = useState<any[]>(dataRows || []);
  const { setLoading } = useAuth();

  const tableClassName = cn(
    'h-full',
    '[&_tbody_td]:px-3 [&_tbody_td]:py-1',
    '[&_tbody_td>div]:min-w-0 [&_tbody_td>div]:py-0.5 [&_tbody_td>div]:leading-[1.25]'
  );

  useEffect(() => {
    const nextRows = dataRows || [];
    setRows(nextRows);
    setSelectedRowIds(previousIds => previousIds.filter(id => nextRows.some(row => String(row?.uuid || '') === id)));
  }, [dataRows]);

  const executeDelete = async (uuids: string[]) => {
    if (uuids.length === 0) {
      return;
    }

    if (bulkDeleteEndpoint) {
      await api.post(bulkDeleteEndpoint, { list: uuids });
      return;
    }

    if (handleDeleteMany) {
      await handleDeleteMany(uuids);
      return;
    }

    if (handleDelete) {
      for (const uuid of uuids) {
        await handleDelete(uuid);
      }
      return;
    }

    throw new Error('BulkDeleteDialogV4 requires bulkDeleteEndpoint, handleDeleteMany, or handleDelete.');
  };

  const handleBulkDelete = async () => {
    try {
      setLoading(true);
      await executeDelete(selectedRowIds);
      setRows(previousRows => previousRows.filter(row => !selectedRowIds.includes(row.uuid || '')));
      setSelectedRowIds([]);
      await onComplete?.();
    } catch (error) {
      notifyBulkDeleteError(error);
    } finally {
      setLoading(false);
    }
  };

  const tables = useMemo(
    () => buildBulkDeleteTables(rows, columns || [], selectedRowIds, setSelectedRowIds),
    [columns, rows, selectedRowIds]
  );

  return (
    <AritoDialog
      open={true}
      onClose={onClose}
      title={title}
      maxWidth='lg'
      disableBackdropClose={false}
      disableEscapeKeyDown={true}
      titleIcon={<AritoIcon icon={279} />}
    >
      <AritoForm
        mode='view'
        hasAritoActionBar={false}
        className='h-[75vh] w-[60vw] min-w-[900px]'
        tabs={
          <ErpDataTableV2
            tables={tables}
            hideHeader={true}
            showIndexColumn={true}
            className={tableClassName}
            getRowId={row => row?.uuid || ''}
            toolbar={<ErpDataTableBulkDeleteActionBar handleDeleteRow={handleBulkDelete} handlePin={() => {}} />}
          />
        }
        bottomBar={<BottomActionBarV2 mode='view' onClose={onClose} />}
      />
    </AritoDialog>
  );
};

export default BulkDeleteDialogV4;
